import "./_ProductEdit.scss";
import "./_ProductEditMobile.scss";
import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CustomDatePicker from "./CustomDatePicker";
import { FaTrash, FaTrashAlt } from "react-icons/fa";

function _ProductEdit({ setActivePanel, setEditIndex, editIndex }) {
  const fileInputRef = useRef(null);

  // ---------- State ----------
  const [images, setImages] = useState([]);
  const [categoryVal, setCategoryVal] = useState("");
  const [productNameVal, setProductNameVal] = useState("");
  const [manufacturerVal, setManufacturerVal] = useState("");
  const [animeSeriesVal, setAnimeSeriesVal] = useState("");
  const [productTypeVal, setProductTypeVal] = useState("ON HAND");
  const [origPriceVal, setOrigPriceVal] = useState("");
  const [salePriceVal, setSalePriceVal] = useState("");
  const [stockVal, setStockVal] = useState("");
  const [japanEstDate, setJapanEstDate] = useState("");
  const [preOrderDeadlineVal, setpreOrderDeadlineVal] = useState("");
  const [downPaymentVal, setDownPaymentVal] = useState("");
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [animeSeries, setAnimeSeries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);

  // Focus states for dropdowns
  const [categoryFocused, setCategoryFocused] = useState(false);
  const [manufacturerFocused, setManufacturerFocused] = useState(false);
  const [animeSeriesFocused, setAnimeSeriesFocused] = useState(false);

  // ---------- Drag & Drop ----------
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(images);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setImages(reordered);
  };

  // ---------- File Handling ----------
  const handleSelectImages = () => fileInputRef.current.click();
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    appendFiles(files);
    e.target.value = null;
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    appendFiles(files);
  };
  const handleDragOver = (e) => e.preventDefault();

  const appendFiles = (files) => {
    const allowedExt = ["jpg", "jpeg", "png", "webp", "heic", "heif"];
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB

    const validFiles = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();

      if (!allowedExt.includes(ext)) return false;

      if (file.size > MAX_SIZE) {
        alert(`${file.name} exceeds 5MB`);
        return false;
      }

      return true;
    });

    const newItems = validFiles.map((file) => {
      const ext = file.name.split(".").pop().toLowerCase();

      return {
        id: `new-${crypto.randomUUID()}`,
        src: ["heic", "heif"].includes(ext)
          ? "/images/image-placeholder.png" // fallback preview
          : URL.createObjectURL(file),
        file,
        name: file.name,
      };
    });

    setImages((prev) => [...prev, ...newItems].slice(0, 6));
  };

  const handleRemoveImage = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleRemoveAll = () => {
    if (window.confirm("Remove all images?")) {
      setImages([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ---------- Utility ----------
  const formatToMMDDYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // ---------- Fetch dropdowns ----------
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/add-get-foreign`,
        );
        const data = await res.json();
        if (data) {
          setCategories(data.categories);
          setManufacturers(data.manufacturers);
          setAnimeSeries(data.animeSeries);
          setDataLoading(true);
        }
      } catch (err) {
        console.error("Failed to fetch dropdown data", err);
      }
    };
    fetchDropdowns();
  }, []);

  // ---------- Load product details ----------
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!editIndex || !dataLoading) return;

      try {
        setLoadingImages(true);
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/get-product-by-id/${editIndex}`,
        );
        const data = await res.json();
        if (!data.product) return;

        const product = data.product;

        // Map existing images
        const imageUrls = Array.isArray(product.PD_ProductPicPath)
          ? product.PD_ProductPicPath.map((img) => ({
              id: img,
              src: `${process.env.REACT_APP_BASE_URL}Assets/Products/${product.PD_ID}/${img}`,
              file: null,
              name: img,
            }))
          : [];

        setImages(imageUrls);

        const categoryObj = categories.find((c) => c.PC_ID === product.PC_ID);
        const manufacturerObj = manufacturers.find(
          (m) => m.PM_ID === product.PM_ID,
        );
        const animeSeriesObj = animeSeries.find(
          (a) => a.AS_ID === product.AS_ID,
        );

        setCategoryVal(categoryObj?.PC_Name || "");
        setProductNameVal(product.PD_Name || "");
        setManufacturerVal(manufacturerObj?.PM_Name || "");
        setAnimeSeriesVal(animeSeriesObj?.AS_Name || "");
        setProductTypeVal(
          product.PD_ProductType === "onhand" ? "ON HAND" : "PRE-ORDER",
        );
        setOrigPriceVal(product.PD_OriginalPrice || "");
        setSalePriceVal(product.PD_SalePrice || "");
        setStockVal(product.PD_Stock || "");
        setJapanEstDate(product.PD_JapanEstmated || "");
        setpreOrderDeadlineVal(
          product.PD_Preorderdeadline
            ? formatToMMDDYYYY(product.PD_Preorderdeadline)
            : "",
        );
        setDownPaymentVal(product.PD_DownPayment || "");
      } catch (err) {
        console.error("Failed to fetch product details", err);
      } finally {
        setLoadingImages(false);
      }
    };
    fetchProductDetails();
  }, [editIndex, dataLoading, categories, manufacturers, animeSeries]);

  // ---------- Validation ----------
  const validateForm = () => {
    let newErrors = {};
    if (!categoryVal) newErrors.categoryVal = "Category is required";
    if (!productNameVal) newErrors.productNameVal = "Product Name is required";
    if (!manufacturerVal)
      newErrors.manufacturerVal = "Manufacturer is required";
    if (!animeSeriesVal) newErrors.animeSeriesVal = "Anime Series is required";
    if (!origPriceVal) newErrors.origPriceVal = "Original Price is required";

    if (productTypeVal === "ON HAND") {
      if (!salePriceVal) newErrors.salePriceVal = "Sales Price is required";
      if (!stockVal) newErrors.stockVal = "Stock is required";
    } else {
      if (!preOrderDeadlineVal)
        newErrors.preOrderDeadlineVal = "Pre-order Deadline is required";
      if (!japanEstDate)
        newErrors.japanEstDate = "Japan Release Date is required";
    }

    if (!downPaymentVal) newErrors.downPaymentVal = "Downpayment is required";
    if (images.length === 0)
      newErrors.productImages = "At least 1 image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- Save ----------
  const handleSave = async () => {
    if (!validateForm()) return;
    if (loadingImages) {
      alert("Please wait, images are still loading.");
      return;
    }

    setLoading(true);

    try {
      const categoryObj = categories.find((c) => c.PC_Name === categoryVal);
      const manufacturerObj = manufacturers.find(
        (m) => m.PM_Name === manufacturerVal,
      );
      const animeObj = animeSeries.find((a) => a.AS_Name === animeSeriesVal);

      if (!categoryObj || !manufacturerObj || !animeObj) {
        alert("Please select valid category, manufacturer, and anime series.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("PC_ID", categoryObj.PC_ID);
      formData.append("PM_ID", manufacturerObj.PM_ID);
      formData.append("AS_ID", animeObj.AS_ID);
      formData.append("PD_Name", productNameVal);
      formData.append("PD_ProductType", productTypeVal);
      formData.append("PD_OriginalPrice", origPriceVal || 0);
      formData.append(
        "PD_SalePrice",
        productTypeVal === "ON HAND" ? salePriceVal || 0 : 0,
      );
      formData.append(
        "PD_Stock",
        productTypeVal === "ON HAND" ? stockVal || 0 : 0,
      );
      formData.append(
        "PD_JapanEst",
        productTypeVal === "PRE-ORDER" ? japanEstDate : "",
      );
      formData.append(
        "PD_PreOrderDeadline",
        productTypeVal === "PRE-ORDER" ? preOrderDeadlineVal : "",
      );
      formData.append("PD_DownPayment", downPaymentVal || 0);

      // ---------------------------
      // Send ordered images
      // ---------------------------
      const orderedImages = images.map((img) => ({
        name: img.name,
        isNew: !!img.file,
      }));
      formData.append("existingImages", JSON.stringify(orderedImages));

      // Append new files
      images
        .filter((img) => img.file)
        .forEach((img) => {
          formData.append("PD_ProductPicPath", img.file, img.name);
        });

      const res = await fetch(
        `https://littlekidinside.shop/api/product/edit-product/${editIndex}`,
        {
          method: "PUT",
          body: formData,
          credentials: "include",
        },
      );

      const data = await res.json();
      if (data.success) {
        alert(data.message || "Product updated successfully.");
        setActivePanel(0);
        setEditIndex(null);
      } else {
        alert(data.error || "Failed to update product.");
      }
    } catch (err) {
      console.error("Error updating product:", err);
      alert("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCategoryVal("");
    setProductNameVal("");
    setManufacturerVal("");
    setAnimeSeriesVal("");
    setProductTypeVal("ON HAND");
    setOrigPriceVal("");
    setSalePriceVal("");
    setStockVal("");
    setJapanEstDate("");
    setpreOrderDeadlineVal("");
    setDownPaymentVal("");
    setImages([]);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    setActivePanel(0);
    setEditIndex(null);
  };

  // ---------- JSX ----------

  // Highlighted item index for keyboard navigation
  const [highlightedCategoryIndex, setHighlightedCategoryIndex] = useState(0);
  const [highlightedManufacturerIndex, setHighlightedManufacturerIndex] =
    useState(0);
  const [highlightedAnimeIndex, setHighlightedAnimeIndex] = useState(0);

  const handleDropdownKey = (
    e,
    list,
    val,
    setVal,
    setFocus,
    highlightedIndex,
    setHighlightedIndex,
    keyProp,
    nameProp,
  ) => {
    const filtered = list.filter((item) =>
      item[nameProp].toLowerCase().includes(val.toLowerCase()),
    );

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filtered.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filtered.length - 1,
      );
    } else if (e.key === "Enter" && filtered.length > 0) {
      setVal(filtered[highlightedIndex][nameProp]);
      setFocus(false);
    }
  };

  return (
    <div className="ep-container">
      <div className="ep-first-row">
        {/* LEFT COLUMN */}
        <div className="ep-first-col">
          {/* Product Name */}
          <div className="ep-input-wrapper">
            <label className="ep-input-label">Product Name:</label>
            <textarea
              className="ep-input"
              value={productNameVal}
              onChange={(e) => setProductNameVal(e.target.value)}
            />
            {errors.productNameVal && (
              <span className="ep-error">{errors.productNameVal}</span>
            )}
          </div>

          {/* Category (searchable) */}
          <div className="ep-input-wrapper">
            <label className="ep-input-label">Category:</label>
            <input
              type="text"
              value={categoryVal}
              onChange={(e) => {
                setCategoryVal(e.target.value);
                setErrors((p) => ({ ...p, categoryVal: "" }));
                setHighlightedCategoryIndex(0);
              }}
              onKeyDown={(e) =>
                handleDropdownKey(
                  e,
                  categories,
                  categoryVal,
                  setCategoryVal,
                  setCategoryFocused,
                  highlightedCategoryIndex,
                  setHighlightedCategoryIndex,
                  "PC_ID",
                  "PC_Name",
                )
              }
              onFocus={() => setCategoryFocused(true)}
              onBlur={() => setTimeout(() => setCategoryFocused(false), 150)}
              placeholder="Type or select category..."
            />

            {categoryFocused && (
              <div className="ep-keyword-result">
                {categories
                  .filter((item) =>
                    item.PC_Name.toLowerCase().includes(
                      categoryVal.toLowerCase(),
                    ),
                  )
                  .map((item, index) => (
                    <div
                      className={`keyword-result ${
                        index === highlightedCategoryIndex ? "highlighted" : ""
                      }`}
                      key={item.PC_ID}
                      onMouseDown={() => {
                        setCategoryVal(item.PC_Name);
                        setCategoryFocused(false);
                      }}
                      ref={(el) => {
                        if (index === highlightedCategoryIndex && el) {
                          el.scrollIntoView({ block: "nearest" });
                        }
                      }}
                    >
                      {item.PC_Name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Manufacturer (searchable) */}
          <div className="ep-input-wrapper">
            <label className="ep-input-label">Manufacturer:</label>
            <input
              type="text"
              value={manufacturerVal}
              onChange={(e) => {
                setManufacturerVal(e.target.value);
                setErrors((p) => ({ ...p, manufacturerVal: "" }));
                setHighlightedManufacturerIndex(0);
              }}
              onKeyDown={(e) =>
                handleDropdownKey(
                  e,
                  manufacturers,
                  manufacturerVal,
                  setManufacturerVal,
                  setManufacturerFocused,
                  highlightedManufacturerIndex,
                  setHighlightedManufacturerIndex,
                  "PM_ID",
                  "PM_Name",
                )
              }
              onFocus={() => setManufacturerFocused(true)}
              onBlur={() =>
                setTimeout(() => setManufacturerFocused(false), 150)
              }
              placeholder="Type or select manufacturer..."
            />

            {manufacturerFocused && (
              <div className="ep-keyword-result">
                {manufacturers
                  .filter((item) =>
                    item.PM_Name.toLowerCase().includes(
                      manufacturerVal.toLowerCase(),
                    ),
                  )
                  .map((item, index) => (
                    <div
                      className={`keyword-result ${
                        index === highlightedManufacturerIndex
                          ? "highlighted"
                          : ""
                      }`}
                      key={item.PM_ID}
                      ref={(el) => {
                        if (index === highlightedManufacturerIndex && el) {
                          el.scrollIntoView({ block: "nearest" });
                        }
                      }}
                      onMouseDown={() => {
                        setManufacturerVal(item.PM_Name);
                        setManufacturerFocused(false);
                      }}
                    >
                      {item.PM_Name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Anime Series (searchable) */}
          <div className="ep-input-wrapper">
            <label className="ep-input-label">Anime Series:</label>
            <input
              type="text"
              value={animeSeriesVal}
              onChange={(e) => {
                setAnimeSeriesVal(e.target.value);
                setErrors((p) => ({ ...p, animeSeriesVal: "" }));
                setHighlightedAnimeIndex(0);
              }}
              onKeyDown={(e) =>
                handleDropdownKey(
                  e,
                  animeSeries,
                  animeSeriesVal,
                  setAnimeSeriesVal,
                  setAnimeSeriesFocused,
                  highlightedAnimeIndex,
                  setHighlightedAnimeIndex,
                  "AS_ID",
                  "AS_Name",
                )
              }
              onFocus={() => setAnimeSeriesFocused(true)}
              onBlur={() => setTimeout(() => setAnimeSeriesFocused(false), 150)}
              placeholder="Type or select anime series..."
            />

            {animeSeriesFocused && (
              <div className="ep-keyword-result">
                {animeSeries
                  .filter((item) =>
                    item.AS_Name.toLowerCase().includes(
                      animeSeriesVal.toLowerCase(),
                    ),
                  )
                  .map((item, index) => (
                    <div
                      className={`keyword-result ${
                        index === highlightedAnimeIndex ? "highlighted" : ""
                      }`}
                      key={item.AS_ID}
                      onMouseDown={() => {
                        setAnimeSeriesVal(item.AS_Name);
                        setAnimeSeriesFocused(false);
                      }}
                      ref={(el) => {
                        if (index === highlightedAnimeIndex && el) {
                          el.scrollIntoView({ block: "nearest" });
                        }
                      }}
                    >
                      {item.AS_Name}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        {/* ... keep all other JSX exactly the same (Product Type, Prices, Images, Buttons, etc.) ... */}

        {/* RIGHT COLUMN */}
        <div className="ep-first-col">
          {/* Product Type */}
          <div className="ep-input-wrapper">
            <label className="ep-input-label">Product Type:</label>
            <select
              value={productTypeVal}
              onChange={(e) => setProductTypeVal(e.target.value)}
            >
              <option value="ON HAND">ON HAND</option>
              <option value="PRE-ORDER">PRE-ORDER</option>
            </select>
          </div>

          {/* Original Price */}
          <div className="ep-input-wrapper">
            <label className="ep-input-label">Original Price:</label>
            <input
              type="text"
              value={origPriceVal}
              onChange={(e) => setOrigPriceVal(e.target.value)}
            />
          </div>

          {productTypeVal === "ON HAND" ? (
            <>
              <div className="ep-input-wrapper">
                <label className="ep-input-label">Sales Price:</label>
                <input
                  type="text"
                  value={salePriceVal}
                  onChange={(e) => setSalePriceVal(e.target.value)}
                />
              </div>
              <div className="ep-input-wrapper">
                <label className="ep-input-label">Number of Stock:</label>
                <input
                  type="text"
                  value={stockVal}
                  onChange={(e) => setStockVal(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              <div className="ep-input-wrapper">
                <label className="ep-input-label">Pre-Order Deadline:</label>
                <CustomDatePicker
                  date={preOrderDeadlineVal}
                  setDate={setpreOrderDeadlineVal}
                />
              </div>
              <div className="ep-input-wrapper">
                <label className="ep-input-label">
                  Japan Estimated Release Date:
                </label>
                <input
                  type="text"
                  value={japanEstDate}
                  onChange={(e) => setJapanEstDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="ep-input-wrapper">
            <label className="ep-input-label">Downpayment:</label>
            <input
              type="text"
              value={downPaymentVal}
              onChange={(e) => setDownPaymentVal(e.target.value)}
            />
          </div>
        </div>

        {/* Drag & Drop Images */}
        <div
          className="ep-image-col"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {images.length > 0 && (
            <span className="ep-remove-all" onClick={handleRemoveAll}>
              <FaTrashAlt />
            </span>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="images-droppable" direction="vertical">
              {(provided) => (
                <div
                  className="ep-image-col-wrapper"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {images.length > 0 ? (
                    images.map((img, index) => (
                      <Draggable
                        key={img.id}
                        draggableId={img.id.toString()}
                        index={index}
                      >
                        {(dragProvided) => (
                          <div
                            className="ep-image-item-wrapper"
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                          >
                            <div className="ep-image-wrapper">
                              <img
                                src={img.src}
                                alt={`Preview ${index}`}
                                onError={(e) =>
                                  (e.target.src =
                                    "https://via.placeholder.com/150")
                                }
                              />
                            </div>
                            <div className="ep-img-file-name">{img.name}</div>
                            <span
                              className="ep-trash-icon"
                              onClick={() => handleRemoveImage(img.id)}
                            >
                              <FaTrash />
                            </span>
                          </div>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <div className="ep-image-prev">
                      Drag & Drop Images Here or Click "UPLOAD IMAGES"
                    </div>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="ep-input-wrapper">
            <button onClick={handleSelectImages}>UPLOAD IMAGES</button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleFileChange}
            />
            <p>FILE EXTENSION: .JPG, .JPEG, .PNG (max 6 images)</p>
            {errors.productImages && (
              <span className="ep-error">{errors.productImages}</span>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="ep-btn-action-wrapper">
          <button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="save-btn" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default _ProductEdit;
